import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { CredentialRow, NationalityPicker } from "./components/CredentialRow";

export default function AdminCredentials() {
  const { t } = useI18n();
  const [games, setGames] = useState([]);
  const [creds, setCreds] = useState([]);
  const [rankings, setRankings] = useState({}); // credId -> rank|null
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterGame, setFilterGame] = useState("__all__");
  const [filterLevel, setFilterLevel] = useState("__all__");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ game_id: "", level: 1, tier_id: null, quantity: 1, email: "", school: "", nationality: "PT" });
  const [sortKey, setSortKey] = useState("ranking");
  const [sortDir, setSortDir] = useState("asc"); // asc | desc
  const [diplomaDialog, setDiplomaDialog] = useState(null); // { credential_id, username, hasBestTime } | null
  const [editing, setEditing] = useState(null); // credential being edited or null
  const [editForm, setEditForm] = useState({ name: "", school: "", nationality: "PT", attempts_available: 0, extra_attempts: 0 });
  const [editAttempts, setEditAttempts] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // Map of game_id → game for tier resolution.
  const gameMap = useMemo(() => {
    const m = {};
    games.forEach((g) => { m[g.id] = g; });
    return m;
  }, [games]);

  const tierLabel = (gameId, level, tierId) => {
    const g = gameMap[gameId];
    if (g && g.level_system === "tiers" && Array.isArray(g.tiers)) {
      let t = tierId ? g.tiers.find((x) => x.id === tierId) : null;
      if (!t) t = g.tiers.find((x) => Number(x.level) === Number(level));
      if (t && t.name) return t.name;
    }
    return level;
  };

  // When filtering by a specific game and that game uses tiers, the level
  // filter should expose the tier names instead of 1-5.
  const filterGameObj = filterGame !== "__all__" ? gameMap[filterGame] : null;
  const useTiersFilter = !!(filterGameObj && filterGameObj.level_system === "tiers" && Array.isArray(filterGameObj.tiers) && filterGameObj.tiers.length > 0);

  const buildDiplomaUrl = (credId) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("admin_token") || sessionStorage.getItem("token") || localStorage.getItem("token") || "";
    // Admin-only endpoint requires Bearer token, so this URL is fetched programmatically (not just clicked).
    return { adminUrl: `${base}/api/diplomas/admin/by-credential/${credId}`, publicUrl: `${base}/api/diplomas/by-credential/${credId}`, token };
  };

  const openDiploma = useCallback(async (cred) => {
    try {
      const res = await api.get(`/diplomas/admin/by-credential/${cred.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      // Open in a new tab so the admin can preview AND download from the browser viewer.
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        // Popup blocked: fall back to forced download.
        const a = document.createElement("a");
        a.href = url;
        a.download = `diploma-${cred.username}.pdf`;
        a.click();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a obter diploma");
    }
  }, []);

  const downloadDiplomaAdmin = async () => {
    if (!diplomaDialog) return;
    try {
      const res = await api.get(`/diplomas/admin/by-credential/${diplomaDialog.credential_id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `diploma-${diplomaDialog.username}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a obter diploma");
    }
  };

  const loadRankings = async (gameFilter) => {
    try {
      const params = {};
      if (gameFilter && gameFilter !== "__all__") params.game_id = gameFilter;
      const { data } = await api.get("/credentials/rankings", { params });
      setRankings(data || {});
    } catch (e) {
      setRankings({});
    }
  };

  const load = async () => {
    const params = {};
    if (filterGame !== "__all__") params.game_id = filterGame;
    if (debouncedSearch) params.search = debouncedSearch;
    const { data } = await api.get("/credentials", { params });
    setCreds(data);
    loadRankings(filterGame);
  };
  // Stable handle so memoised row callbacks never go stale.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    api.get("/games").then((r) => {
      // The Credenciais Competição page manages logins for the yearly
      // competition only, so the games dropdown MUST be scoped to the
      // games whose `status` is "competition". Eliminatórias, VS Tempo
      // and training games are not relevant here and were leaking in
      // (reported Feb-2026).
      const compGames = (r.data || []).filter((g) => g?.status === "competition");
      setGames(compGames);
      if (compGames.length) setForm((f) => ({ ...f, game_id: compGames[0].id }));
    });
  }, []);

  // Debounce the search box: typing stays instant (local state), while the
  // actual backend fetch + full-table re-render only fires 350ms after the
  // last keystroke. Critical once there are thousands of credentials.
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => { load(); }, [filterGame, debouncedSearch]);

  // When changing game (or switching between tier/non-tier games), reset the
  // level/tier filter to avoid carrying a stale tier id that no longer exists
  // in the new game's tiers list.
  useEffect(() => { setFilterLevel("__all__"); }, [filterGame]);

  // Map game_id -> max_attempts for the "tentativas usadas" computation.
  const gameMaxMap = useMemo(() => {
    const m = {};
    games.forEach((g) => { m[g.id] = g.max_attempts || 3; });
    return m;
  }, [games]);

  const usedAttempts = (c) => {
    const max = gameMaxMap[c.game_id] || 3;
    const extra = c.extra_attempts || 0;
    const remaining = c.attempts_available ?? max;
    // Used = (total allowed) − remaining.
    return Math.max(0, max + extra - remaining);
  };

  // Filter rows client-side by selected level/tier.
  //   "__all__"           → all
  //   "1".."5"            → match credentials with that level number
  //   "tier:<id>"         → match credentials with that specific tier_id
  //                         (falls back to matching tier.level for legacy
  //                         credentials lacking tier_id)
  const filteredCreds = useMemo(() => {
    if (filterLevel === "__all__") return creds;
    if (filterLevel.startsWith("tier:")) {
      const tid = filterLevel.slice(5);
      const tier = useTiersFilter ? filterGameObj.tiers.find((t) => t.id === tid) : null;
      const lv = tier ? Number(tier.level) : null;
      return creds.filter((c) => {
        if (c.tier_id) return c.tier_id === tid;
        return lv != null && Number(c.level || 1) === lv;
      });
    }
    const lv = parseInt(filterLevel, 10);
    return creds.filter((c) => (c.level || 1) === lv);
  }, [creds, filterLevel, useTiersFilter, filterGameObj]);

  const openEdit = useCallback(async (c) => {
    setEditing(c);
    setEditForm({
      name: c.name || "",
      school: c.school || "",
      nationality: (c.nationality || "PT").toUpperCase(),
      attempts_available: c.attempts_available ?? 0,
      extra_attempts: c.extra_attempts ?? 0,
    });
    setEditAttempts([]);
    setEditLoading(true);
    try {
      const { data } = await api.get(`/credentials/${c.id}/attempts`);
      setEditAttempts(data?.attempts || []);
    } catch (_) { /* ignore */ }
    finally { setEditLoading(false); }
  }, []);

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/credentials/${editing.id}`, {
        name: editForm.name,
        school: editForm.school,
        nationality: editForm.nationality.toUpperCase(),
        attempts_available: parseInt(editForm.attempts_available, 10) || 0,
        extra_attempts: parseInt(editForm.extra_attempts, 10) || 0,
      });
      toast.success("Alterações guardadas");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar");
    }
  };

  const generate = async () => {
    try {
      await api.post("/credentials/generate", form);
      toast.success("Credenciais geradas");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const remove = useCallback(async (id) => {
    if (!window.confirm("Eliminar?")) return;
    await api.delete(`/credentials/${id}`);
    setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
    loadRef.current();
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const toggleSelectAllVisible = (visibleIds) => {
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
    setSelected((s) => {
      const n = new Set(s);
      if (allSelected) visibleIds.forEach((id) => n.delete(id));
      else visibleIds.forEach((id) => n.add(id));
      return n;
    });
  };

  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Eliminar ${selected.size} credencial(is) selecionada(s)?`)) return;
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/credentials/bulk-delete", { ids });
      toast.success(`${data.deleted} eliminada(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a eliminar");
    }
  };

  const deleteAllFiltered = async () => {
    // Apaga tudo o que está atualmente visível na tabela (respeitando filtros de jogo/nível/pesquisa)
    const ids = sortedCreds.map((c) => c.id);
    if (!ids.length) return;
    const scopeLabel = [];
    if (filterGame !== "__all__") scopeLabel.push(games.find((g) => g.id === filterGame)?.name || "jogo");
    if (filterLevel !== "__all__") {
      if (filterLevel.startsWith("tier:")) {
        const tid = filterLevel.slice(5);
        const tier = useTiersFilter ? filterGameObj.tiers.find((t) => t.id === tid) : null;
        scopeLabel.push(tier?.name || "escalão");
      } else {
        scopeLabel.push(`Nível ${filterLevel}`);
      }
    }
    if (search) scopeLabel.push(`pesquisa "${search}"`);
    const scope = scopeLabel.length ? ` (${scopeLabel.join(" · ")})` : " (TODAS)";
    if (!window.confirm(`Eliminar ${ids.length} credencial(is)${scope}? Esta ação não pode ser revertida.`)) return;
    try {
      const { data } = await api.post("/credentials/bulk-delete", { ids });
      toast.success(`${data.deleted} eliminada(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a eliminar");
    }
  };

  // Immutable local edit → only the affected (memoised) row re-renders.
  const patchLocal = useCallback((id, patch) => {
    setCreds((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }, []);

  const updateInline = useCallback(async (id, patch) => {
    await api.put(`/credentials/${id}`, patch);
    loadRef.current();
  }, []);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedCreds = useMemo(() => {
    const arr = [...filteredCreds];
    const dir = sortDir === "asc" ? 1 : -1;
    const INF = Number.POSITIVE_INFINITY;
    arr.sort((a, b) => {
      let va, vb;
      if (sortKey === "username") { va = a.username || ""; vb = b.username || ""; return va.localeCompare(vb) * dir; }
      if (sortKey === "name") { va = a.name || ""; vb = b.name || ""; return va.localeCompare(vb) * dir; }
      if (sortKey === "school") { va = a.school || ""; vb = b.school || ""; return va.localeCompare(vb) * dir; }
      if (sortKey === "level") { va = a.level || 0; vb = b.level || 0; return (va - vb) * dir; }
      if (sortKey === "best_time") {
        va = a.best_time == null ? INF : a.best_time;
        vb = b.best_time == null ? INF : b.best_time;
        return (va - vb) * dir;
      }
      if (sortKey === "ranking") {
        va = rankings[a.id] == null ? INF : rankings[a.id];
        vb = rankings[b.id] == null ? INF : rankings[b.id];
        return (va - vb) * dir;
      }
      if (sortKey === "attempts") {
        // Sort by USED attempts (computed) so it matches what's displayed.
        const ua = (gameMaxMap[a.game_id] || 3) + (a.extra_attempts || 0) - (a.attempts_available ?? 0);
        const ub = (gameMaxMap[b.game_id] || 3) + (b.extra_attempts || 0) - (b.attempts_available ?? 0);
        return (ua - ub) * dir;
      }
      if (sortKey === "extra_attempts") { return ((a.extra_attempts ?? 0) - (b.extra_attempts ?? 0)) * dir; }
      if (sortKey === "victories") { return ((a.victories ?? 0) - (b.victories ?? 0)) * dir; }
      if (sortKey === "defeats") { return ((a.defeats ?? 0) - (b.defeats ?? 0)) * dir; }
      return 0;
    });
    return arr;
  }, [filteredCreds, rankings, sortKey, sortDir]);

  // Rendering thousands of rows (each with inputs + a nationality <Select>) is
  // what makes the page feel heavy. Cap the DOM to the first MAX_RENDER rows;
  // selection and "delete all filtered" still operate on the full set. Users
  // narrow down via the game filter + search to reach specific rows.
  const MAX_RENDER = 300;
  const visibleCreds = useMemo(() => sortedCreds.slice(0, MAX_RENDER), [sortedCreds]);
  const hiddenCount = sortedCreds.length - visibleCreds.length;

  const sortIcon = (key) => {
    if (sortKey !== key) return <ArrowUpDown className="w-3 h-3 inline opacity-40 ml-1" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3 inline ml-1" /> : <ArrowDown className="w-3 h-3 inline ml-1" />;
  };

  const sortHeader = (k, label, testid) => (
    <th
      data-testid={testid}
      onClick={() => toggleSort(k)}
      className={`text-left px-3 py-3 text-xs uppercase font-bold cursor-pointer select-none hover:bg-slate-100 ${sortKey === k ? "text-sky-700" : "text-slate-700"}`}
    >
      {label}{sortIcon(k)}
    </th>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900">{t("credentials")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-gen-cred" className="bg-slate-900"><Plus className="w-4 h-4 mr-1" /> {t("generate")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("generate")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t("competition")}</Label>
                <Select value={form.game_id} onValueChange={(v) => setForm({ ...form, game_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{(() => {
                    const g = gameMap[form.game_id];
                    return g?.level_system === "tiers" ? "Nível/Escalão" : t("level");
                  })()}</Label>
                  {(() => {
                    const g = gameMap[form.game_id];
                    if (g?.level_system === "tiers" && Array.isArray(g.tiers) && g.tiers.length > 0) {
                      // Use tier.id as value (avoids the "both selected" bug when
                      // multiple tiers share the same `level` number).
                      const currentTierId = form.tier_id || g.tiers[0]?.id;
                      return (
                        <Select
                          value={currentTierId}
                          onValueChange={(id) => {
                            const tr = g.tiers.find((x) => x.id === id);
                            setForm({ ...form, tier_id: id, level: tr ? parseInt(tr.level, 10) || 1 : form.level });
                          }}
                        >
                          <SelectTrigger data-testid="gen-level"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {g.tiers.map((tr) => (
                              <SelectItem key={tr.id} value={tr.id}>{tr.name} <span className="text-slate-400 text-xs">(Nv {tr.level})</span></SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }
                    return <Input type="number" min={1} max={5} value={form.level} onChange={(e) => setForm({ ...form, level: +e.target.value, tier_id: null })} />;
                  })()}
                </div>
                <div><Label>{t("quantity")}</Label><Input type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: +e.target.value })} /></div>
              </div>
              <div><Label>{t("email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>{t("school")}</Label><Input value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} /></div>
              <div><Label>{t("nationality")}</Label><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value.toUpperCase() })} /></div>
              <Button data-testid="btn-confirm-gen" onClick={generate} className="w-full">{t("generate")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <Input placeholder="Pesquisar..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" data-testid="cred-search" />
        <Select value={filterGame} onValueChange={setFilterGame}>
          <SelectTrigger className="max-w-xs" data-testid="cred-filter-game"><SelectValue placeholder="Filtrar jogo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os jogos</SelectItem>
            {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterLevel} onValueChange={setFilterLevel}>
          <SelectTrigger className="max-w-[200px]" data-testid="cred-filter-level"><SelectValue placeholder={useTiersFilter ? "Filtrar escalão" : "Filtrar nível"} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{useTiersFilter ? "Todos os escalões" : "Todos os níveis"}</SelectItem>
            {useTiersFilter
              ? filterGameObj.tiers.map((tr) => (
                  <SelectItem key={tr.id} value={`tier:${tr.id}`}>{tr.name} <span className="text-slate-400 text-xs">(Nv {tr.level})</span></SelectItem>
                ))
              : [1, 2, 3, 4, 5].map((lv) => (
                  <SelectItem key={lv} value={String(lv)}>Nível {lv}</SelectItem>
                ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2 items-center">
          <Button
            data-testid="btn-cred-delete-selected"
            disabled={!selected.size}
            onClick={deleteSelected}
            variant="outline"
            className="text-rose-700 border-rose-300 hover:bg-rose-50 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Apagar selecionadas ({selected.size})
          </Button>
          <Button
            data-testid="btn-cred-delete-all"
            disabled={!sortedCreds.length}
            onClick={deleteAllFiltered}
            variant="outline"
            className="text-rose-700 border-rose-400 hover:bg-rose-50 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Apagar tudo ({sortedCreds.length})
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  data-testid="cred-select-all"
                  className="w-4 h-4 cursor-pointer"
                  checked={sortedCreds.length > 0 && sortedCreds.every((c) => selected.has(c.id))}
                  onChange={() => toggleSelectAllVisible(sortedCreds.map((c) => c.id))}
                />
              </th>
              {sortHeader("username", "Username", "sort-username")}
              <th className="text-left px-3 py-3 text-xs uppercase font-bold">Password</th>
              {sortHeader("name", "Nome", "sort-name")}
              {sortHeader("school", "Escola", "sort-school")}
              <th className="text-left px-3 py-3 text-xs uppercase font-bold">Email</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold">Nacionalidade</th>
              {sortHeader("level", "Nível/Escalão", "sort-level")}
              {sortHeader("best_time", "Melhor Tempo", "sort-best-time")}
              {sortHeader("ranking", "Ranking", "sort-ranking")}
              {sortHeader("attempts", "Tent. usadas", "sort-attempts")}
              {sortHeader("extra_attempts", "Extra", "sort-extra-attempts")}
              {sortHeader("victories", "Vit.", "sort-victories")}
              {sortHeader("defeats", "Der.", "sort-defeats")}
              <th className="text-left px-3 py-3 text-xs uppercase font-bold">Diploma</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold"></th>
            </tr>
          </thead>
          <tbody data-testid="credentials-table">
            {visibleCreds.map((c) => (
              <CredentialRow
                key={c.id}
                c={c}
                rank={rankings[c.id]}
                isSel={selected.has(c.id)}
                levelLabel={tierLabel(c.game_id, c.level, c.tier_id)}
                used={usedAttempts(c)}
                allowed={(gameMaxMap[c.game_id] || 3) + (c.extra_attempts || 0)}
                onToggleSelect={toggleSelect}
                onLocalChange={patchLocal}
                onCommit={updateInline}
                onDiploma={openDiploma}
                onEdit={openEdit}
                onRemove={remove}
              />
            ))}
            {!sortedCreds.length && <tr><td colSpan={16} className="text-center py-6 text-slate-400">Sem credenciais</td></tr>}
            {hiddenCount > 0 && (
              <tr>
                <td colSpan={16} className="text-center py-3 text-slate-500 text-xs bg-slate-50" data-testid="cred-truncated-hint">
                  A mostrar as primeiras {MAX_RENDER} de {sortedCreds.length} credenciais. Refine a pesquisa ou filtros para ver outras. (As ações "Apagar tudo" e "selecionar todas" aplicam-se às {sortedCreds.length}.)
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit credential dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Editar {editing?.username}
              {editing && (
                <span className="text-xs font-normal text-slate-500 ml-2">
                  · {games.find((g) => g.id === editing.game_id)?.name || ""} · {(() => {
                    const g = gameMap[editing.game_id];
                    const isTiers = g?.level_system === "tiers" && Array.isArray(g.tiers);
                    const name = isTiers ? (g.tiers.find((x) => Number(x.level) === Number(editing.level))?.name) : null;
                    return name || `Nível ${editing.level}`;
                  })()}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome</Label>
                <Input data-testid="edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div>
                <Label>Escola</Label>
                <Input data-testid="edit-school" value={editForm.school} onChange={(e) => setEditForm({ ...editForm, school: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Nacionalidade</Label>
                <NationalityPicker
                  testid="edit-nationality"
                  value={editForm.nationality}
                  onChange={(v) => setEditForm((f) => ({ ...f, nationality: v }))}
                />
              </div>
              <div>
                <Label>Tentativas restantes</Label>
                <Input data-testid="edit-attempts" type="number" min={0} value={editForm.attempts_available} onChange={(e) => setEditForm({ ...editForm, attempts_available: +e.target.value })} />
                <div className="text-[11px] text-slate-500 mt-1">
                  {editing ? `Máx do jogo: ${gameMaxMap[editing.game_id] || 3}` : ""}
                </div>
              </div>
              <div>
                <Label>Extra</Label>
                <Input data-testid="edit-extra" type="number" min={0} value={editForm.extra_attempts} onChange={(e) => setEditForm({ ...editForm, extra_attempts: +e.target.value })} />
                <div className="text-[11px] text-slate-500 mt-1">Tentativas adicionais permitidas</div>
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Histórico de tentativas</Label>
              <div className="border border-slate-200 rounded-lg max-h-[260px] overflow-auto bg-slate-50">
                {editLoading ? (
                  <div className="text-center text-slate-400 py-6 text-sm">A carregar…</div>
                ) : editAttempts.length === 0 ? (
                  <div className="text-center text-slate-400 py-6 text-sm">Sem tentativas registadas.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-white border-b sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-bold text-slate-600">#</th>
                        <th className="text-left px-3 py-2 text-xs font-bold text-slate-600">Estado</th>
                        <th className="text-left px-3 py-2 text-xs font-bold text-slate-600">Tempo (s)</th>
                        <th className="text-left px-3 py-2 text-xs font-bold text-slate-600">Terminada em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editAttempts.map((a, idx) => (
                        <tr key={a.id || `${a.created_at}-${idx}`} className="border-b last:border-0">
                          <td className="px-3 py-1.5 font-mono text-xs">{editAttempts.length - idx}</td>
                          <td className="px-3 py-1.5">
                            <span className={`inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-full ${
                              a.status === "won" ? "bg-emerald-100 text-emerald-700"
                              : a.status === "lost" ? "bg-rose-100 text-rose-700"
                              : "bg-slate-200 text-slate-700"
                            }`}>
                              {a.status === "won" ? "Vitória" : a.status === "lost" ? "Derrota" : "Em curso"}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 font-mono text-xs">{a.time_seconds != null ? a.time_seconds.toFixed(2) : "—"}</td>
                          <td className="px-3 py-1.5 text-xs text-slate-600">{a.finished_at ? new Date(a.finished_at).toLocaleString() : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="edit-cancel">Cancelar</Button>
              <Button onClick={saveEdit} data-testid="edit-save" className="bg-slate-900">Guardar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
